import React, { useEffect, useState } from "react";
import {
  Body,
  Layout,
  LoadingIndicator,
  ProductCollection,
  ProductTile,
  Spacer,
  // StickyContainer,
  UtilityBar,
  Chip,
  ChipGroup,
  ButtonToggle,
  FlagText,
  Image,
  Price,
  RatingStatic
} from "@common/AggregationComponent";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import type { CombinedReducersType, ProductDetail } from "@type/index";
import { PORDUCT_SORT, SimilarProducts } from "@type/index";
import { isGUNativeApp } from "@util/nativeApp";
import {
  sendTrackEvent,
  sendTrackPageView,
  TRACK_EVENT,
  TRACK_EVENT_SEARCH
} from "@util/gtm";
import {
  checkPreviousPageExists,
  formatPrice,
  transformBrandForGoogleAnalytics,
  getEcRenewalCompatibility,
  getEcRenewalImageRatio,
  localizeSizeGenderName
} from "@util/common";
import { getUA, isIOS, isSafari as isSafariBrowser } from "react-device-detect";
import { useCookies } from "react-cookie";
import { FR_UserAgent } from "@util/config";
import { handleFlagsSort } from "./flagsConfig";
import FilterNoResult from "../../../common/FilterNoResult";
import style from "../style.module.scss";

export type ProductListPros = {
  visible: boolean;
  filterCount: number | string;
  products: ProductDetail[];
  total: number | string;
  isImageLoad: boolean;
  onToggleFilter: () => void;
  onChangeSort: (sort: string) => void;
  onClickFavorite: (product: ProductDetail, index: number) => void;
  // visual Products
  similarProducts: SimilarProducts[];
  currentSort: string;
  setIsClickProductTabSort: any;
  filterPillContent: any;
};
export type Sort = {
  label: string;
  value: string;
};

const ProductList = (props: ProductListPros) => {
  const {
    visible,
    total,
    filterCount,
    products,
    onToggleFilter,
    onChangeSort,
    onClickFavorite,
    isImageLoad,
    similarProducts,
    currentSort,
    setIsClickProductTabSort,
    filterPillContent
  } = props;
  const { t } = useTranslation();
  const location = useLocation();
  const [, setCookie] = useCookies(["jumpFrom"]);
  const config = useSelector((store: CombinedReducersType) => store.app);
  const BASE_PATH_NAME = `/${config.region}/${config.language}`;
  const loadingStatus = useSelector(
    (state: CombinedReducersType) => state.searchResult.resultItemloading
  );
  const [sortList, setSortList] = useState<Sort[]>([]);
  useEffect(() => {
    setSortList([
      {
        label: `${t("text_image_search_sorting_similarity")}`,
        value: PORDUCT_SORT.SIMILAR
      },
      {
        label: `${t("text_image_search_sort_latest")}`,
        value: PORDUCT_SORT.RECENCY
      }
    ]);
  }, [t]);
  useEffect(() => {
    const isFinalTitle =
      document.title === t("text_image_search_result_meta_title");
    const isNotDefaultTitle =
      document.title !== "text_image_search_result_meta_title";
    if (window.dataLayer && isFinalTitle && isNotDefaultTitle) {
      sendTrackPageView({
        event: "trackPageView",
        brand: transformBrandForGoogleAnalytics(config.brand_name), // UQの場合"uq"
        pageType: "imagesearch", // TPUの場合TPU
        subID: "", // subid取得できる場合subidを出力、できない場合undefined
        pageUrlPath: `${location.pathname}${location.search}`, // path+param
        pageTitle: t("text_image_search_result_meta_title"), // title
        previousPageExists: checkPreviousPageExists() // 前のページがある場合true、ない場合false
      });
    }
  }, [document.title, t, location]);
  const getChipValues = (imagesObj: any, colorsObj: any) => {
    const urls: any[] = [];
    const keys = Object.keys(imagesObj)
      .filter((key: any) => {
        let res = false;
        colorsObj.map((color: any) => {
          if (color.displayCode === key) {
            res = true;
          }
          return null;
        });
        return res;
      })
      .sort((a: string, b: string) => parseInt(a, 10) - parseInt(b, 10));
    keys.forEach((key: string) => {
      if (imagesObj[key]) {
        urls.push(imagesObj[key]);
      }
    });
    return urls;
  };
  const filterProductToProductTile = (
    product: ProductDetail,
    index: number
  ): any => {
    // Find the pictures that match
    const img = JSON.parse(JSON.stringify(product.images));
    const imgChip = Object.keys(img.chip);
    const imgMain = Object.keys(img.main);
    const getRepeat = (a: any[], b: any[]) => {
      const result = [];
      for (const i of a) {
        if (b.indexOf(i) !== -1) {
          result.push(i);
        }
      }
      return result;
    };
    const chipData = getRepeat(imgChip, imgMain);
    if (
      chipData.length !== imgChip.length ||
      chipData.length !== imgMain.length
    ) {
      const newImgChip: any = {};
      const newImgMain: any = {};
      for (const indexChip of chipData) {
        newImgChip[indexChip] = img.chip[indexChip];
        newImgMain[indexChip] = img.main[indexChip];
      }
      product.images.chip = newImgChip;
      product.images.main = newImgMain;
    }
    const chipImages = getChipValues(product.images.chip, product.colors).map(
      (url: any) => ({
        url,
        alt: "url"
      })
    );
    const getCurrentPrice = () => {
      let price: string;
      if (!product.prices.promo) {
        price = `${t("currency_format", {
          price: formatPrice(product.prices.base.value)
        })}`;
      } else {
        price = `${t("currency_format", {
          price: formatPrice(product.prices.promo.value)
        })}`;
      }
      return price;
    };
    const images = getChipValues(product.images.main, product.colors)
      .filter((item: any) => item.image)
      .map((item: any) => `${item.image}?width=402&height=402`);
    const getSizeRange = () => {
      const sizes = product.sizes.map((size: any) => size.name);
      if (sizes.length > 1) {
        return [sizes[0], sizes[sizes.length - 1]];
      }
      return sizes;
    };
    const newFlags = handleFlagsSort(
      {
        promotionText: product.promotionText,
        priceFlag: product.representative.flags.priceFlags
      },
      product.representative.flags.productFlags,
      t
    );
    const getProductFlags = () => {
      const productFlags = product.representative.flags.productFlags;
      if (productFlags.length > 0) {
        return productFlags.map((flag: any) => ({
          ...productFlags,
          flag: flag.name,
          type: "normal"
        }));
      }
      return [];
    };
    const priceFlags = newFlags.price_flag?.map((item) => ({
      ...item,
      type: item.color === "promotional" ? "limited" : "normal"
    }));
    const getColorCode = (): string => {
      const currentSimilarProduct = similarProducts.find(
        (prod: SimilarProducts) =>
          prod.upstream_product_id === product.productId
      );
      return currentSimilarProduct ? currentSimilarProduct.color_code : "";
    };

    const getDeafultColorIndex = (): number => {
      const colorCode = getColorCode();
      if (colorCode) {
        // fix Object.keys sort issue
        const chipIndex = Object.keys(product.images.chip)
          .filter((key: any) => {
            let res = false;
            product.colors.map((color: any) => {
              if (color.displayCode === key) {
                res = true;
              }
              return null;
            });
            return res;
          })
          .sort((a: string, b: string) => parseInt(a, 10) - parseInt(b, 10))
          .findIndex((chip: string) => chip === colorCode);
        if (chipIndex > -1) return chipIndex;
      }
      return 0;
    };

    const getParams = () => {
      const paramsList = [];
      if (getColorCode()) {
        paramsList.push(["colorDisplayCode", getColorCode()]);
      }
      if (
        product.representative &&
        product.representative.size &&
        product.representative.size.displayCode
      ) {
        paramsList.push([
          "sizeDisplayCode",
          product.representative.size.displayCode
        ]);
      }

      return `?${new URLSearchParams(paramsList).toString()}`;
    };

    return {
      id: product.productId,
      name: product.name,
      imageUrl: images[getDeafultColorIndex()]
        ? images[getDeafultColorIndex()]
        : "",
      images,
      chipImages,
      genderName: [
        localizeSizeGenderName(t, product.sizeGender, product.genderCategory) ??
          ""
      ],
      currentPrice: getCurrentPrice(),
      enableChipInteraction: true,
      // productId: product.productId,
      flags: getProductFlags(), // "standard" grey "promotional" red
      priceHighlighted: product.prices ? product.prices.promo !== null : false, // price color
      priceFlags, // price flag & color
      // promotionText: product.promotionText,
      sizeRange: getSizeRange(),
      l1Id: product.l1Id,
      productLink: `${window.location.origin}${BASE_PATH_NAME}/products/${
        product.productId
      }/${product.priceGroup}${getParams()}`,
      reviewUrl: `${window.location.origin}${BASE_PATH_NAME}/products/${product.productId}/${product.priceGroup}/reviews`,
      ratingAverage: product.rating.average,
      ratingCount: product.rating.count,
      favoriteIconAlt: "Add to Wish List", // refer of www.uniqlo.com/us/en/search
      onClickFavorite: () => {
        onClickFavorite(product, index);
      },
      // promoPriceTextType={"KingPromoApplied"}
      favorite: false,
      // TODO: Note!When the wish function is unified in the APP, the judgment here needs to be processed.
      favoriteButtonHidden:
        (config.isApp && !isGUNativeApp()) || getUA.indexOf(FR_UserAgent) > -1,
      // current chip index
      currentChip: getDeafultColorIndex(),
      gaCallback: () => {
        setCookie("jumpFrom", "product", { path: "/" });
        const htmlDom = document.querySelector("html");
        const isSafari = isSafariBrowser || isIOS;
        if (htmlDom && !isSafari) {
          sessionStorage.setItem(
            "productScrollTop",
            htmlDom.scrollTop.toString()
          );
        }
        sendTrackEvent({
          brand: transformBrandForGoogleAnalytics(config.brand_name),
          event: TRACK_EVENT_SEARCH,
          eventAction: "Click_L4",
          eventCategory: "IS_Result_Item",
          eventLabel: `${index}_${product.productId}`,
          itemIndex: `${index}`,
          itemId: `${product.representative.l2Id}`
        });
      },
      errorMessage: `${t("text_no_image")}`
    };
  };
  const getProductTiles = () => {
    const renderList: any = [];
    if (products.length > 0) {
      products.forEach((product: ProductDetail, index: number) => {
        if (product) {
          renderList.push(
            <ProductTile {...filterProductToProductTile(product, index)} />
          );
        }
      });
    }
    return renderList;
  };

  const getProductTilesForUI3 = () => {
    const renderList: any = [];
    if (products.length > 0) {
      products.forEach((product: ProductDetail, index: number) => {
        if (product) {
          renderList.push(
            <ProductTile
              media={
                <Image
                  src={filterProductToProductTile(product, index).imageUrl}
                  noImage={{
                    imageSize: "default",
                    imageType: "product",
                    noImageLabel: t("text_no_image")
                  }}
                  ratio={getEcRenewalImageRatio()}
                  ecRenewal
                />
              }
              favoriteButtonHidden={false}
              type="wishlist"
              chipGroup={
                <ChipGroup
                  id={`${product.l1Id}-chip-group`}
                  ecRenewal
                  variant="productTile"
                  children={
                    product.images &&
                    product.colors.map((chipValue: any, chipIndex: any) => (
                      <Chip
                        id={`${product.l1Id}-chip-${chipIndex}`}
                        image={product.images.chip[chipValue.displayCode]}
                        label=""
                        type="radio"
                        name="shortChipGroup"
                        ecRenewal
                        variant="product-tile"
                      />
                    ))
                  }
                />
              }
              id={product.productId}
              name={product.name}
              productLink={
                filterProductToProductTile(product, index).productLink
              }
              ecRenewal
              genderName={[
                localizeSizeGenderName(
                  t,
                  product.sizeGender,
                  product.genderCategory
                ) ?? ""
              ]}
              sizeRange={filterProductToProductTile(product, index).sizeRange}
              {...(!filterProductToProductTile(product, index)
                .favoriteButtonHidden && {
                buttonToggle: (
                  <ButtonToggle
                    icon="heart"
                    variant="icon"
                    isToggled={false}
                    onClick={() => onClickFavorite(product, index)}
                  />
                )
              })}
              informationContent={
                <div>
                  <Price
                    element="p"
                    size="middle"
                    variant={
                      filterProductToProductTile(product, index)
                        .priceHighlighted
                        ? "promotional"
                        : "standard"
                    }
                    className="fr-ec-mt-spacing-01"
                  >
                    {filterProductToProductTile(product, index).currentPrice}
                  </Price>
                  <div className="fr-ec-mb-spacing-01">
                    {filterProductToProductTile(product, index).priceFlags?.map(
                      (f: any, i: any) => (
                        <FlagText
                          key={f.flag}
                          element="span"
                          variant="product-tile"
                          color={
                            f.type === "limited" ? "promotional" : "placeholder"
                          }
                        >
                          {i + 1 <
                          filterProductToProductTile(product, index).priceFlags
                            .length
                            ? `${f.flag}, `
                            : f.flag}
                        </FlagText>
                      )
                    )}
                    <FlagText
                      element="p"
                      variant="product-tile"
                      color="placeholder"
                    >
                      {filterProductToProductTile(product, index)
                        .flags.map((f: any) => f.flag)
                        .join(", ")}
                    </FlagText>
                  </div>
                  {product.rating.count > 0 && (
                    <RatingStatic
                      ariaLabel="ariaLabel"
                      reviews={product.rating.count}
                      value={parseFloat(product.rating.average)}
                      variant="oneStar"
                      averageValue={parseFloat(product.rating.average)}
                    >
                      <>{`(${
                        product.rating.count && product.rating.count > 999
                          ? "999+"
                          : product.rating.count
                      })`}</>
                    </RatingStatic>
                  )}
                </div>
              }
              gaCallback={() => {
                setCookie("jumpFrom", "product", { path: "/" });
                const htmlDom = document.querySelector("html");
                const isSafari = isSafariBrowser || isIOS;
                if (htmlDom && !isSafari) {
                  sessionStorage.setItem(
                    "productScrollTop",
                    htmlDom.scrollTop.toString()
                  );
                }
                sendTrackEvent({
                  brand: transformBrandForGoogleAnalytics(config.brand_name),
                  event: TRACK_EVENT_SEARCH,
                  eventAction: "Click_L4",
                  eventCategory: "IS_Result_Item",
                  eventLabel: `${index}_${product.productId}`,
                  itemIndex: `${index}`,
                  itemId: `${product.representative.l2Id}`
                });
              }}
            />
          );
        } else {
          renderList.push(<></>);
        }
      });
    }
    return renderList;
  };
  const handlerChangeSort = (e: any, value: string) => {
    if (value === "0") {
      sendTrackEvent({
        brand: transformBrandForGoogleAnalytics(config.brand_name),
        event: TRACK_EVENT,
        eventAction: "ImageItemSearch",
        eventCategory: "IS_Item_Sort",
        eventLabel: `${t("text_image_search_sorting_similarity")}`
      });
      setIsClickProductTabSort(true);
      onChangeSort(PORDUCT_SORT.SIMILAR);
    }
    if (value === "1") {
      sendTrackEvent({
        brand: transformBrandForGoogleAnalytics(config.brand_name),
        event: TRACK_EVENT,
        eventAction: "ImageItemSearch",
        eventCategory: "IS_Item_Sort",
        eventLabel: `${t("text_image_search_sort_latest")}`
      });
      setIsClickProductTabSort(true);
      onChangeSort(PORDUCT_SORT.RECENCY);
    }
  };

  const getProductCollectionList = () => {
    if (getProductTiles().length > 0 && !loadingStatus) {
      const productScrollTop = sessionStorage.getItem("productScrollTop");
      if (productScrollTop) {
        const isSafari = isSafariBrowser || isIOS;

        const scrollTop = parseInt(productScrollTop, 10);
        if (
          typeof window !== "undefined" &&
          "scrollRestoration" in window.history &&
          !isSafari
        ) {
          setTimeout(() => {
            window.scrollTo(0, scrollTop);
            sessionStorage.removeItem("productScrollTop");
          }, 300);
        }
      }
    }
    if (getProductTiles().length === 0 && !loadingStatus) {
      return <FilterNoResult />;
    } else {
      return (
        <>
          {loadingStatus && <LoadingIndicator variant="full-screen" />}
          <ProductCollection
            variant="grid"
            gaCategory="ga-category"
            type="grid"
            products={
              getEcRenewalCompatibility()
                ? getProductTilesForUI3()
                : getProductTiles()
            }
            ecRenewal
          />
        </>
      );
    }
  };

  const checkNewUI3 = () => {
    if (getEcRenewalCompatibility()) {
      return (
        <Layout style={{ width: "100%" }} className={style.layout_with_utility}>
          {isImageLoad && (
            // <StickyContainer
            //   stickySection="utilityBar"
            //   isNavHeader={!config.isApp}
            // >
            <UtilityBar
              resultDetailsSection={
                <Body variant="standard" element="span">
                  {t("text_image_search_result_number", {
                    total: total
                  })}
                </Body>
              }
              isNavHeader={!config.isApp}
              sortLabel={visible ? t("text_sort") : ""}
              toggleSort={visible}
              toggleFilter={false}
              onToggleFilter={onToggleFilter}
              handleChangeSort={handlerChangeSort}
              badgeCount={filterCount}
              filterBadgeCount={filterCount}
              currentSortValue={currentSort}
              sortOptions={sortList}
              className={style.filter_button_box}
              filterPillContent={filterPillContent}
              closeLabel={getEcRenewalCompatibility() ? " " : t("text_close")}
              collapseFilterLabel={undefined}
              filterIcon="filter"
              onClickFilterIcon={onToggleFilter}
              ecRenewal
            />
            // </StickyContainer>
          )}
          {getProductCollectionList()}
          <Spacer element="div" variant={"spacing-08"} />
        </Layout>
      );
    } else {
      return (
        <Layout style={{ width: "100%" }} className={style.layout_with_utility}>
          {isImageLoad && (
            // <StickyContainer
            //   stickySection="utilityBar"
            //   isNavHeader={!config.isApp}
            // >
            <UtilityBar
              resultDetailsSection={
                <Body variant="standard" element="span">
                  {t("text_image_search_result_number", {
                    total: total
                  })}
                </Body>
              }
              isNavHeader={!config.isApp}
              sortLabel={visible ? t("text_sort") : ""}
              collapseFilterLabel={t("text_filter")}
              toggleSort={visible}
              toggleFilter={false}
              onToggleFilter={onToggleFilter}
              handleChangeSort={handlerChangeSort}
              badgeCount={filterCount}
              filterBadgeCount={filterCount}
              currentSortValue={currentSort}
              sortOptions={sortList}
              className={style.filter_button_box}
              filterPillContent={filterPillContent}
              closeLabel={t("text_close")}
            />
            // </StickyContainer>
          )}
          {getProductCollectionList()}
          <Spacer element="div" variant={"spacing-08"} />
        </Layout>
      );
    }
  };

  return checkNewUI3();
};

export default ProductList;
